import { useEffect, useState, useCallback } from "react";
import { DeleteOutlined, EditOutlined, ExclamationCircleFilled, PlusOutlined } from "@ant-design/icons";
import { Modal, Space, Row, Col, Button, Table, Tag } from "antd";
import AddSheetMapping from "../components/sheet-mapping/AddSheetMapping";
import { deleteData, getData } from "../helpers/api";
import withRoleProtection from "../components/withRoleProtection";
import { openNotification } from "../helpers/alerts";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import TextInput from "../components/inputs/TextInput";
import SelectInput from "../components/inputs/SelectInput";
import { CiSearch } from "react-icons/ci";
import { RxReset } from "react-icons/rx";
import { rolesConst } from "../constants/others";
import { getLocalStorage } from "../helpers/localstorage";

const { confirm } = Modal;

const filterSchema = Yup.object({
    sheetName: Yup.string().nullable().optional(),
    productName: Yup.string().nullable().optional(),
});

const INITIAL_FILTER = {
    sheetName: "",
    productName: "",
};

const INITIAL_VALUES = {
    sheetName: "",
    product: null,
    nameColumn: "",
    phoneColumn: "",
    addressColumn: "",
    priceColumn: "",
    orderDateColumn: "",
    unitColumn: "",
    sheetId:null,
};

const SheetMapping = () => {
    const [sheetMapping, setSheetMapping] = useState(INITIAL_VALUES);
    const [sheetMappings, setSheetMappings] = useState([]);
    const [filter, setFilter] = useState({});
    const [totalSheetMappings, setTotalSheetMappings] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(false);

    const user = getLocalStorage("auth")?.user;
    const isSuperAdmin = user?.role === rolesConst.SuperAdmin;

    
    const showDeleteConfirm = (sheetMappingId) => {
        confirm({
            title: "Vous êtes sûr de supprimer ce mapping de feuille ?",
            icon: <ExclamationCircleFilled />,
            okText: "Supprimer",
            okType: "danger",
            cancelText: "Annuler",
            onOk() {
                deleteSheetMapping(sheetMappingId);
            },
        });
    };

    const deleteSheetMapping = async (sheetMappingId) => {
        try {
            await deleteData(`/sheet-mapping/${sheetMappingId}`);
            openNotification(`Le mapping de feuille a été supprimé avec succès`, "success");
            getSheetMappings();
        } catch (error) {
            openNotification(`Erreurs côté serveur`, "error");
        }
    };

    const columns = [
        {
            title: "Nom de la feuille",
            dataIndex: "sheetName",
            key: "sheetName",
            width: 180,
        },
        {
            title: "Produit",
            dataIndex: "productName",
            key: "productName",
            width: 150,
            render: (productName) => (
                <Tag color="blue">{productName || 'N/A'}</Tag>
            ),
        },
        {
            title: "Colonne Nom",
            dataIndex: "nameColumn",
            key: "nameColumn",
            width: 130,
        },
        {
            title: "Colonne Téléphone",
            dataIndex: "phoneColumn",
            key: "phoneColumn",
            width: 150,
        },
        {
            title: "Colonne Adresse",
            dataIndex: "addressColumn",
            key: "addressColumn",
            width: 140,
        },
        {
            title: "Colonne Prix",
            dataIndex: "priceColumn",
            key: "priceColumn",
            width: 120,
        },
        {
            title: "Colonne Date Commande",
            dataIndex: "orderDateColumn",
            key: "orderDateColumn",
            width: 170,
        },
        {
            title: "Colonne Unité",
            dataIndex: "unitColumn",
            key: "unitColumn",
            width: 130,
        },
        {
            title: "colonne ID",
            dataIndex: "sheetId",
            key: "sheetId",
            width: 130,
        },
        {
            title: "Actions",
            dataIndex: "actions",
            key: "actions",
            width: 120,
            fixed: 'right',
            render: (_, record) => {
                return (
                    <Space size="middle">
                        <span>
                            <EditOutlined onClick={() => setSheetMapping({ ...record, edit: true })} />
                        </span>
                        <span>
                            <DeleteOutlined onClick={() => showDeleteConfirm(record.id)} />
                        </span>
                    </Space>
                );
            },
        },
    ];

    const getSheetMappings = useCallback(async (page = 1, limit = 10, filterParams = {}) => {
        try {
            setLoading(true);

            const user = getLocalStorage("auth")?.user;
            const isConfirmationConvertion = user?.role === rolesConst.ConfirmationConvertion;

            if (isConfirmationConvertion) {
                // For ConfirmationConvertion role, get only one record
                limit = 1;
                page = 1;
            }

            const response = await getData(`/sheet-mapping`);

            // Handle direct array response or paginated response
            let data;
            if (Array.isArray(response.data)) {
                data = response.data;
            } else {
                data = response.data.data || response.data;
            }

            // Apply client-side filtering if needed
            let filteredData = data;
            if (filterParams.sheetName) {
                filteredData = filteredData.filter(item =>
                    item.sheetName.toLowerCase().includes(filterParams.sheetName.toLowerCase())
                );
            }
            if (filterParams.productName) {
                filteredData = filteredData.filter(item =>
                    item.product?.name?.toLowerCase().includes(filterParams.productName.toLowerCase())
                );
            }

            // For ConfirmationConvertion, only show one record
            if (isConfirmationConvertion && filteredData.length > 0) {
                filteredData = [filteredData[0]];
            }

            const mappedData = filteredData.map((item) => ({
                id: item.id,
                sheetName: item.sheetName,
                productName: item.product?.name || 'N/A',
                product: item.product,
                columnMapping: item.columnMapping,
                nameColumn: item.columnMapping?.name || 0,
                phoneColumn: item.columnMapping?.phone || 0,
                addressColumn: item.columnMapping?.address || 0,
                priceColumn: item.columnMapping?.price || 0,
                orderDateColumn: item.columnMapping?.orderDate || 0,
                unitColumn: item.columnMapping?.unit || 0,
                sheetId: item.columnMapping?.sheetId || 0,
            }));

            // Apply pagination client-side if needed
            const startIndex = (page - 1) * limit;
            const endIndex = startIndex + limit;
            const paginatedData = mappedData.slice(startIndex, endIndex);

            setSheetMappings(paginatedData);
            setTotalSheetMappings(mappedData.length);
            setCurrentPage(page);
            setPageSize(limit);
        } catch (error) {
            console.error("Error fetching sheet mappings:", error);
            openNotification("Erreur lors du chargement des mappings", "error");
        } finally {
            setLoading(false);
        }
    }, []);

    const getProducts = useCallback(async () => {
        try {
            const res = await getData("/products");
            setProducts(res.data.map((product) => ({ value: product.name, label: product.name })));
        } catch (error) {
            console.log(error);
        }
    }, []);

    const handleCancel = () => {
        setSheetMapping(INITIAL_VALUES);
    };

    const handleAfterAddingSheetMapping = () => {
        getSheetMappings(currentPage, pageSize, filter);
        handleCancel();
    };

    const handleTableChange = (pagination) => {
        if (isSuperAdmin) {
            getSheetMappings(pagination.current, pagination.pageSize, filter);
        }
    };

    const handleFilter = (values) => {
        setFilter(values);
        setCurrentPage(1);
        getSheetMappings(1, pageSize, values);
    };

    const handleResetFilter = () => {
        setFilter(INITIAL_FILTER);
        setCurrentPage(1);
        getSheetMappings(1, pageSize, {});
    };

    useEffect(() => {
        getSheetMappings();
        getProducts();
    }, [getSheetMappings, getProducts]);

    const code = `
        const WEBHOOK_URL = 'https://amgs.digital/google-sheets/webhook';
        function envoyerDonnees() {
        const sheetName = 'Feuille 2'; // Your sheet name
        const sentColumnIndex = 8; // H column for "Sent"
        const sheet = SpreadsheetApp.getActiveSpreadsheet().getSheetByName(sheetName);
        if (!sheet) return;

        const dataRange = sheet.getDataRange();
        const data = dataRange.getValues();

        // Skip header row (assumed to be row 1)
        for (let i = 1; i < data.length; i++) {
            const row = data[i];

            // Skip if already marked as sent in column H
            if (row[sentColumnIndex - 1] === 'Sent') continue;

            const payload = {
            ligne: i + 1,
            donnees: row.slice(0, sentColumnIndex - 1), // Exclude the Sent column
            };

            const body = {
            evenement: 'batch-scheduled-envoi',
            nomFeuille: sheetName,
            lignes: [payload],
            };

            const options = {
            method: 'POST',
            contentType: 'application/json',
            payload: JSON.stringify(body),
            };
            
            try {
            const response = UrlFetchApp.fetch(webhookUrl, options);
            const result = JSON.parse(response.getContentText());

            if (result.success) {
                // Mark as sent in fixed column (H)
                sheet.getRange(i + 1, sentColumnIndex).setValue('Sent');
            } else {
                Logger.log(\`Failed to send row \${i + 1}: \${result.message}\`);
            }
            } catch (e) {
            Logger.log(\`Error sending row \${i + 1}: \${e.message}\`);
            }
        }
        }`;

    return (
        <>
            <p>
                {code}
            </p>
            {(sheetMapping.add || sheetMapping.edit) && (
                <AddSheetMapping
                    handleAfterAddingSheetMapping={handleAfterAddingSheetMapping}
                    initialValue={sheetMapping}
                    isModalOpen={sheetMapping.add || sheetMapping.edit}
                    handleCancel={handleCancel}
                />
            )}

            <Row gutter={[16, 16]} style={{ marginBottom: "20px" }}>
                <Col span={24}>
                    <h2>Mapping des Feuilles</h2>
                </Col>
            </Row>

            {/* Show add button and filters only for SuperAdmin */}
            {isSuperAdmin && (
                <Row gutter={[16, 16]} style={{ marginBottom: "20px" }}>
                    <Col span={24}>
                        <Formik
                            initialValues={INITIAL_FILTER}
                            validationSchema={filterSchema}
                            onSubmit={handleFilter}
                        >
                            <Form>
                                <Row gutter={[16, 16]} align="middle">
                                    <Col xl={4} lg={5} md={6} sm={12} xs={24}>
                                        <TextInput
                                            name="sheetName"
                                            placeholder="Nom de la feuille"
                                            size="large"
                                        />
                                    </Col>
                                    <Col xl={4} lg={5} md={6} sm={12} xs={24}>
                                        <SelectInput
                                            name="productName"
                                            size="large"
                                            placeholder="Produit"
                                            options={products}
                                        />
                                    </Col>
                                    <Col xl={3} lg={3} md={4} sm={6} xs={12}>
                                        <button
                                            type="submit"
                                            className="ant-btn ant-btn-primary ant-btn-lg"
                                            style={{ width: "100%", height: "40px", display: "flex", alignItems: "center", justifyContent: "center" }}
                                        >
                                            <CiSearch />
                                            Chercher
                                        </button>
                                    </Col>
                                    <Col xl={3} lg={3} md={4} sm={6} xs={12}>
                                        <button
                                            type="button"
                                            className="ant-btn ant-btn-lg"
                                            onClick={handleResetFilter}
                                            style={{ width: "100%", height: "40px", display: "flex", alignItems: "center", justifyContent: "center" }}
                                        >
                                            <RxReset />
                                            Reset
                                        </button>
                                    </Col>
                                    <Col xl={10} lg={8} md={8} sm={24} xs={24}>
                                        <Button
                                            type="primary"
                                            icon={<PlusOutlined />}
                                            onClick={() => setSheetMapping({ ...INITIAL_VALUES, add: true })}
                                            style={{
                                                height: "40px",
                                                width: "100%"
                                            }}
                                        >
                                            Ajouter un mapping de feuille
                                        </Button>
                                    </Col>
                                </Row>
                            </Form>
                        </Formik>
                    </Col>
                </Row>
            )}

            <Table
                columns={columns}
                dataSource={sheetMappings}
                rowKey="id"
                loading={loading}
                pagination={isSuperAdmin ? {
                    current: currentPage,
                    pageSize: pageSize,
                    total: totalSheetMappings,
                    showSizeChanger: true,
                    pageSizeOptions: ['10', '20', '50', '100'],
                    showQuickJumper: true,
                    showTotal: (total, range) =>
                        `${range[0]}-${range[1]} de ${total} éléments`,
                } : false}
                onChange={handleTableChange}
                scroll={{ x: 1200 }}
                size="small"
            />
        </>
    );
};

export default withRoleProtection(SheetMapping, [rolesConst.SuperAdmin, rolesConst.ConfirmationConvertion]);
